/**
 * Deep-link helpers into the Thrive tenant web app (`nu.thrivetrm.com`
 * — distinct from the API host `api.services.thrivetrm.com`). Consolidates
 * what was previously 2 independently-hardcoded copies of the same domain
 * (`ImportsTable.tsx`, `ImportInfoGrid.tsx`) into one place, per PR #479
 * review — Agent A. `SyncContacts.tsx`'s local `THRIVE_SEARCH_BASE` predates
 * that PR and is still outstanding.
 *
 * `W2ConflictCard.tsx`'s own `THRIVE_CONTACT_BASE_URL` was the last contact-URL
 * copy and is gone (2026-09-08): the conflict cards moved onto a shared shell,
 * which was the moment a FOURTH copy would have been written instead.
 *
 * `thriveContactUrl` (added for the "Thrive ID" column, epic
 * 2026-07-13-contact-enrichment-hellosky) consolidates a THIRD copy that
 * had grown in `ContactsTable.tsx` — `ExportToThrivePanel.tsx` now shares
 * it too rather than adding a fourth.
 *
 * THE BASE IS A VARIABLE, AND ONLY IN LOCAL (2026-09-10)
 * ------------------------------------------------------
 * Locally these links pointed at the real tenant, where the id they carry does
 * not exist — every one of them was a dead end, and the ONE workflow that most
 * wants them is compare: read a collision, go fix the contact, come back and
 * re-check. `VITE_THRIVE_WEB_BASE` (set to the thrive-mock's host port in
 * `.env.local`) makes that a click instead of a `docker exec` with hand-written
 * SQL.
 *
 * It is deliberately NOT a general-purpose override. Dev and prod fall through
 * to the literal below with no variable set, because a mistyped env var must
 * degrade to the real tenant rather than to a host nobody is running — and
 * because `nu.thrivetrm.com/contacts/<id>` being a route you can verify by hand
 * is the property the candidate tables (`CandidatesTable.tsx`, and before it
 * the deleted `AllCandidatesTable.tsx`) rely on for trusting these links at
 * all. The mock's editor answers the same two paths (`/contacts/{id}` under
 * `/ui/`), which is why one base swap is enough.
 */

/** The real tenant. The fallback, so a missing or misspelled variable lands
 *  somewhere true. */
const THRIVE_WEB_BASE_DEFAULT = "https://nu.thrivetrm.com";

/** Trailing slashes stripped: the callers below all join with `/`, and a base
 *  ending in one produced `//contacts/123`, which the mock 404s on. */
function base(): string {
  // A PRODUCTION BUILD IGNORES THE OVERRIDE ENTIRELY.
  //
  // The comment above says "local only" and, until this, nothing enforced it:
  // Vite inlines whatever `VITE_THRIVE_WEB_BASE` holds at build time, so a
  // production bundle built on a machine with `.env.local` present would ship
  // `localhost:8088` as the CRM link for every recruiter. That it has not
  // happened rests on production being built from a clean CI checkout — a
  // property of the pipeline, not of this code (/pr-review iter 1, agent C).
  //
  // `import.meta.env.PROD` is a compile-time constant, so this branch is
  // eliminated from the production bundle along with the variable read.
  if (import.meta.env.PROD) return THRIVE_WEB_BASE_DEFAULT;
  const configured = import.meta.env.VITE_THRIVE_WEB_BASE;
  return (configured || THRIVE_WEB_BASE_DEFAULT).replace(/\/+$/, "");
}

export function thriveSearchCandidatesUrl(searchId: string): string {
  return `${base()}/searches/${searchId}/candidates`;
}

export function thriveContactUrl(contactIdThrive: string): string {
  return `${base()}/contacts/${contactIdThrive}`;
}
